import React, { useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  Chip
} from '@mui/material';
import { Add as AddIcon, Medication as TherapeuticIcon } from '@mui/icons-material';
import { useEntitySearch } from '../../hooks/useEntitySearch';
import { useNotification } from '../../context/NotificationContext';
import {
  searchTherapeutics,
  getTherapeutic,
  createTherapeutic,
  updateTherapeutic,
  deleteTherapeutic,
  batchEnrichTherapeutics
} from '../../api/therapeutics';
import { Therapeutic, TherapeuticCreate, TherapeuticUpdate, TherapeuticListItem } from '../../types/therapeutic';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import EntitySearchResults from '../../components/entities/EntitySearchResults/EntitySearchResults';
import TherapeuticForm from '../../components/entities/TherapeuticForm/TherapeuticForm';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';

const TherapeuticsPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { results, loading: searchLoading, error: searchError, search, clearResults } = useEntitySearch<TherapeuticListItem>(searchTherapeutics);

  const [selectedTherapeutic, setSelectedTherapeutic] = useState<Therapeutic | null>(null);
  const [loadingTherapeutic, setLoadingTherapeutic] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrichingBatch, setEnrichingBatch] = useState(false);

  const handleSearch = useCallback(async (query: string) => {
    await search(query);
  }, [search]);

  const handleSelectTherapeutic = async (item: TherapeuticListItem) => {
    setLoadingTherapeutic(true);
    setError(null);

    try {
      const therapeutic = await getTherapeutic(item.id);
      setSelectedTherapeutic(therapeutic);
      clearResults(); // Clear search results after selection
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load therapeutic');
      showNotification('Failed to load therapeutic', 'error');
    } finally {
      setLoadingTherapeutic(false);
    }
  };

  const handleNewTherapeutic = () => {
    setSelectedTherapeutic(null);
    setError(null);
  };

  const handleSave = async (data: TherapeuticCreate | TherapeuticUpdate, id?: string) => {
    try {
      if (id) {
        const updated = await updateTherapeutic(id, data as TherapeuticUpdate);
        setSelectedTherapeutic(updated);
        showNotification('Therapeutic updated successfully', 'success');
      } else {
        const created = await createTherapeutic(data as TherapeuticCreate);
        setSelectedTherapeutic(created);
        showNotification('Therapeutic created successfully', 'success');
      }
      if (results.length > 0) {
        await search(results[0].name.substring(0, 3));
      }
    } catch (err: any) {
      throw err;
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTherapeutic(id);
      setSelectedTherapeutic(null);
      showNotification('Therapeutic deleted successfully', 'success');
      if (results.length > 0) {
        await search(results[0].name.substring(0, 3));
      }
    } catch (err: any) {
      throw err;
    }
  };

  const handleBulkEnrich = async () => {
    if (results.length === 0) return;

    setEnrichingBatch(true);
    showNotification(`Started processing ${results.length} therapeutics. This might take a few minutes...`, 'info');
    try {
      const ids = results.map(r => r.id);
      await batchEnrichTherapeutics({ therapeutic_ids: ids });
      showNotification(`Successfully enriched ${ids.length} therapeutics.`, 'success');
    } catch (err: any) {
      showNotification('Failed to batch enrich therapeutics', 'error');
    } finally {
      setEnrichingBatch(false);
    }
  };

  const renderTherapeuticItem = (therapeutic: TherapeuticListItem) => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TherapeuticIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {therapeutic.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {therapeutic.generic_name && (
            <Chip label={therapeutic.generic_name} size="small" color="primary" variant="outlined" />
          )}
          {therapeutic.brand_name && (
            <Chip label={therapeutic.brand_name} size="small" color="secondary" variant="outlined" />
          )}
        </Box>
      </Box>
      {therapeutic.summary_description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {therapeutic.summary_description.substring(0, 200)}
          {therapeutic.summary_description.length > 200 ? '...' : ''}
        </Typography>
      )}
      {therapeutic.company && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Company: {therapeutic.company.name}
        </Typography>
      )}
    </Box>
  );

  return (
    <Container maxWidth="xl" className="py-6">
      {/* Top Toolbar */}
      <Box className="flex items-center justify-between gap-4 mb-6">
        <Typography variant="h4" component="h1" className="font-bold text-gray-800 flex items-center gap-2">
          <TherapeuticIcon fontSize="large" className="text-blue-600" />
          Therapeutics
        </Typography>

        <Box className="flex items-center gap-3 flex-1 max-w-2xl">
          <SearchBar
            onSearch={handleSearch}
            placeholder="Search therapeutics by name..."
          />
          {results.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBulkEnrich}
              disabled={enrichingBatch}
              size="large"
            >
              {enrichingBatch ? 'Starting...' : 'Bulk Enrich Visible'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewTherapeutic}
            size="large"
            className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
          >
            New Therapeutic
          </Button>
        </Box>
      </Box>

      {/* Search Loading Indicator */}
      {searchLoading && results.length === 0 && (
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <LoadingSpinner message="Searching therapeutics..." />
        </Paper>
      )}

      {/* Search Results - Show only if we have results */}
      {results.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
            Search Results
          </Typography>

          {searchError && <ErrorMessage message={searchError} />}

          {!searchError && (
            <EntitySearchResults
              results={results}
              onSelect={handleSelectTherapeutic}
              renderItem={renderTherapeuticItem}
            />
          )}
        </Paper>
      )}

      {/* Main Form Section */}
      <Paper elevation={2} className="p-6">
        <Typography variant="h5" className="mb-4 font-semibold text-gray-700 flex items-center gap-2">
          {selectedTherapeutic ? (
            <>
              <TherapeuticIcon className="text-blue-600" />
              Edit Therapeutic: {selectedTherapeutic.name}
            </>
          ) : (
            <>
              <AddIcon className="text-blue-600" />
              Create New Therapeutic
            </>
          )}
        </Typography>

        {loadingTherapeutic && <LoadingSpinner message="Loading therapeutic..." />}

        {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

        {!loadingTherapeutic && (
          <TherapeuticForm
            therapeutic={selectedTherapeutic}
            onSave={handleSave}
            onDelete={handleDelete}
            onCancel={handleNewTherapeutic}
          />
        )}
      </Paper>
    </Container>
  );
};

export default TherapeuticsPage;
